// src/pages/workflows/WorkflowTemplates.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Layers as LayersIcon,
  FileCopy as CopyIcon
} from '@mui/icons-material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import { EntityCard } from '../../design-system/components/data';
import ActionsMenu from '../../components/ui/ActionsMenu';

// Import hooks and stores
import useWorkflowStore from '../../stores/workflowStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Filter configuration
const FILTER_CONFIG = [
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'draft', label: 'Draft' }
    ]
  },
  {
    name: 'type',
    label: 'Type',
    allLabel: 'All Types',
    options: [
      { value: 'contentApproval', label: 'Content Approval' },
      { value: 'campaignApproval', label: 'Campaign Approval' },
      { value: 'budgetApproval', label: 'Budget Approval' },
      { value: 'custom', label: 'Custom' }
    ]
  }
];

// Sort options
const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'type', label: 'Type' },
  { field: 'status', label: 'Status' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'updatedAt', label: 'Last Updated' }
];

/**
 * Workflow Templates page component
 */
const WorkflowTemplates = () => {
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    templates, 
    fetchTemplates, 
    deleteTemplate, 
    isLoading, 
    error,
    meta
  } = useWorkflowStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Check permissions
  const canCreate = hasPermission('workflows', 'create');
  const canUpdate = hasPermission('workflows', 'update');
  const canDelete = hasPermission('workflows', 'delete');
  
  // Handle workflow template deletion with confirmation
  const handleDeleteTemplate = useCallback(async (template) => {
    const confirmed = await confirm({
      title: 'Delete Workflow Template',
      content: `Are you sure you want to delete "${template.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteTemplate(template._id);
        showSuccess(`Workflow template "${template.name}" deleted successfully`);
      } catch (error) {
        showError(`Failed to delete template: ${error.message}`);
      }
    }
  }, [confirm, deleteTemplate, showSuccess, showError]);
  
  // Handle duplicate action
  const handleDuplicate = useCallback((id) => {
    navigate(`/workflows/templates/${id}/duplicate`);
  }, [navigate]);

  // Entity renderer for grid view
  const renderTemplate = useCallback((template) => {
    if (!template || !template._id) return null;
    
    return (
      <>
        <EntityCard
          title={template.name}
          description={template.description || 'No description provided'}
          status={
            <Chip 
              label={template.status === 'active' ? 'Active' : 'Draft'} 
              size="small" 
              color={template.status === 'active' ? 'success' : 'default'} 
            />
          }
          chips={[
            <Chip 
              key="type" 
              label={template.type} 
              size="small" 
               
              variant="outlined" 
            />,
            <Chip 
              key="steps" 
              label={`${template.steps?.length || 0} Steps`} 
              size="small" 
              variant="outlined" 
            />
          ]}
          linkTo={`/workflows/templates/${template._id}`}
          actionButtons={
            <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}>
              <Button 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/workflows/templates/${template._id}`);
                }}
              >
                View Details
              </Button>
              
              <Button
                size="small"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/workflows/instances/create?templateId=${template._id}`);
                }}
              >
                Start Workflow
              </Button>
            </Box>
          }
          menuButtonProps={{
            onClick: (e) => e.stopPropagation() // Let the ActionsMenu handle this
          }}
        />
        
        {/* Separate actions menu */}
        <ActionsMenu
          anchorEl={null} // This will be handled by EntityCard
          actions={[
            {
              label: 'View Details',
              onClick: () => navigate(`/workflows/templates/${template._id}`),
              icon: <ViewIcon fontSize="small" />
            },
            ...(canUpdate ? [{
              label: 'Edit',
              onClick: () => navigate(`/workflows/templates/${template._id}/edit`),
              icon: <EditIcon fontSize="small" />
            }] : []),
            ...(canCreate ? [{
              label: 'Duplicate',
              onClick: () => handleDuplicate(template._id),
              icon: <CopyIcon fontSize="small" />
            }] : [])
          ]}
          dangerActions={canDelete ? [
            {
              label: 'Delete', 
              onClick: () => handleDeleteTemplate(template),
              icon: <DeleteIcon fontSize="small" />
            }
          ] : []}
        />
      </>
    );
  }, [canCreate, canUpdate, canDelete, navigate, handleDuplicate, handleDeleteTemplate]);

  // Define table columns
  const tableColumns = [
    { 
      id: 'name', 
      label: 'Template Name',
      renderCell: (template) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {template.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {template.description || 'No description'}
          </Typography>
        </Box>
      )
    },
    { 
      id: 'type', 
      label: 'Type',
      align: 'center',
      renderCell: (template) => (
        <Chip 
          label={template.type} 
          size="small" 
           
          variant="outlined" 
        />
      )
    },
    { 
      id: 'status', 
      label: 'Status',
      align: 'center',
      renderCell: (template) => (
        <Chip 
          label={template.status === 'active' ? 'Active' : 'Draft'} 
          size="small" 
          color={template.status === 'active' ? 'success' : 'default'} 
        />
      )
    },
    { 
      id: 'steps', 
      label: 'Steps',
      align: 'center',
      renderCell: (template) => (
        <Typography variant="body2">
          {template.steps?.length || 0}
        </Typography>
      )
    },
    { 
      id: 'lastUsed', 
      label: 'Last Used',
      align: 'center',
      renderCell: (template) => (
        <Typography variant="body2">
          {template.lastUsed ? new Date(template.lastUsed).toLocaleDateString() : 'Never'}
        </Typography>
      )
    }
  ];

  // Configure row actions for table view
  const getRowActions = useCallback((template) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="Start Workflow">
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/workflows/instances/create?templateId=${template._id}`);
            }}
          >
            Start
          </Button>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        
        <Tooltip title="View Details">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/workflows/templates/${template._id}`);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {canUpdate && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/workflows/templates/${template._id}/edit`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canCreate && (
          <Tooltip title="Duplicate">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicate(template._id);
              }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTemplate(template);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canCreate, canDelete, navigate, handleDuplicate, handleDeleteTemplate]);
  
  return (
    <>
      <EntityListContainer
        title="Workflow Templates"
        description="Create and manage standardized approval workflows"
        fetchData={fetchTemplates}
        renderEntity={renderTemplate}
        initialFilters={{
          status: 'all',
          type: 'all'
        }}
        initialSort={{ field: 'name', direction: 'asc' }}
        filterConfig={FILTER_CONFIG}
        sortOptions={SORT_OPTIONS}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="grid"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (template) => navigate(`/workflows/templates/${template._id}`),
          renderRowActions: getRowActions
        }}
        
        // Use direct store values
        entities={templates}
        isLoading={isLoading}
        error={error}
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: '/workflows/templates/create',
          createLabel: 'Create Template'
        }}
        emptyState={{
          title: 'No Workflow Templates',
          description: 'Create workflow templates to standardize and automate your approval processes.',
          icon: <LayersIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Templates',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default WorkflowTemplates;